import type { ComponentType, FC, PropsWithChildren } from 'react'
import { useMemo } from 'react'
import styled from '@emotion/styled'
import { css } from '@emotion/react'

import { AppearanceProvider } from '@segment/matcha/foundations/AppearanceContext'

import Loader from '@segment/matcha/components/Loader'

import Footer from '@segment/templates/Footer'
import Navigation from '@segment/templates/Navigation'

import type { GroupedFilters, Customer, Metadata } from '@segment/api/routes/customers/queries'

import { useCustomersQuery } from '@segment/api/routes/customers/queries'

import type { Filter, FiltersState } from '@segment/api/types/common'

import type { NextPageTriggerProps } from '@segment/utils/useNextPageTrigger'
import useNextPageTrigger from '@segment/utils/useNextPageTrigger'

import Layout from '@segment/next-shared/layout'

import CustomerCardsGrid from './components/CustomerCardsGrid'
import Hero from './components/Hero'
import FilterSelect from './components/Filter'
import { useFilters } from './data/useFilters'

const toOptionProps = (filter: Filter) => {
  const { id, title } = filter
  return {
    value: id,
    text: title,
  }
}

const useIntegrations = (
  filtersState: FiltersState,
  triggerComponent?: ComponentType<PropsWithChildren<NextPageTriggerProps>>
) => {
  const { data, isLoading, hasNextPage, fetchNextPage, error, isFetchingNextPage } = useCustomersQuery({
    args: [{ filters: filtersState }],
  })

  const integrations = useMemo(() => data?.pages?.flatMap((collection) => collection?.items ?? []) ?? [], [data?.pages])

  const nextPageTrigger = useNextPageTrigger({
    isLoading,
    totalDownloadedPages: data?.pages.length ?? -1,
    hasNextPage,
    error,
    onNextPage() {
      fetchNextPage()
    },
    loadMargin: `0%`,
    NextPageTrigger: triggerComponent,
  })

  return { integrations, isLoading, nextPageTrigger, isFetchingNextPage, hasNextPage }
}

export interface CustomersViewProps {
  filters: GroupedFilters
  heroData: Customer[]
  metaData: Metadata
}

const CustomersView: FC<PropsWithChildren<CustomersViewProps>> = ({ filters, heroData, metaData }) => {
  const groupedFilterEntries = Object.entries(filters)
  const { filtersState, selectHandlers } = useFilters(groupedFilterEntries)
  const { integrations, isLoading, nextPageTrigger, isFetchingNextPage, hasNextPage } = useIntegrations(filtersState)

  return (
    <AppearanceProvider appearance="twilioLight">
      <Layout
        pageTitle={metaData.socialTitle}
        pageDescription={metaData.socialDescription}
        pageOG={metaData.socialImage.fields.file.url}
      >
        <Navigation />
        <Main>
          <Hero
            data={heroData.map((data) => ({
              ...data,
              featuredLogo: data.matchaLogo?.fields.logoMono,
            }))}
          >
            <Filters>
              {groupedFilterEntries.map(([key, filter]) => (
                <FilterSelect
                  key={key}
                  options={filter.items.map(toOptionProps)}
                  id={key}
                  onSelect={(selectHandlers as any)[key]}
                  placeholder={filter.title}
                />
              ))}
            </Filters>
          </Hero>
          <Wrapper>
            {!isLoading && <CustomerCardsGrid integrations={integrations} />}
            {isLoading && (
              <LoaderWrapper>
                <Loader />
              </LoaderWrapper>
            )}
            {hasNextPage && (
              <LoaderWrapper compensateTopGutter={hasNextPage}>{isFetchingNextPage && <Loader />}</LoaderWrapper>
            )}
            {nextPageTrigger}
          </Wrapper>
        </Main>
      </Layout>
      <Footer />
    </AppearanceProvider>
  )
}

const Main = styled.main`
  background-color: ${({ theme }) => theme.palette.twilio.gray[10]};
`

const Filters = styled.div`
  display: grid;
  row-gap: 16px;
  column-gap: 24px;

  ${({ theme }) => theme.breakpoints.up(`md`)} {
    grid-auto-flow: column;
  }
`

const Wrapper = styled.div`
  max-width: 1084px;
  margin: auto;
`

interface LoaderWrapperProps {
  compensateTopGutter?: boolean
}

const LoaderWrapper = styled.div<LoaderWrapperProps>`
  display: grid;
  align-items: center;
  justify-content: center;
  height: 400px;
  padding: 80px 0;
  margin: 0 auto;
  ${({ compensateTopGutter }) =>
    compensateTopGutter &&
    css`
      padding: 0;
      margin-top: -160px;
    `}
`

export default CustomersView
